import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import { Icon } from "./Icons";
import "../styles/TimesheetDropdown.css";

const DAYS = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
const DAY_SHORT = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const DayCell = ({ isOnLeave, isHoliday, isBeforeStart, canEdit, hours, mode, dayKey, handleChange, handleModeToggle }) => {
  if (isHoliday) return <span className="ts-holiday-indicator">Holiday</span>;
  if (isOnLeave) return <span className="ts-leave-indicator">Leave</span>;
  if (isBeforeStart) return <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", textAlign: "center" }}>—</span>;
  return (
    <>
      {canEdit ? (
        <input type="number" className="ts-hours-input" min="0" max="24" value={hours}
          onChange={(e) => handleChange(dayKey, e.target.value)} aria-label={`Hours for ${dayKey}`}/>
      ) : (
        <span className="ts-hours-display">{hours || 0}</span>
      )}
      {canEdit ? (
        <fieldset className="ts-mode-toggle" aria-label={`Work mode for ${dayKey}`} style={{ border: "none", padding: 0, margin: 0 }}>
          <button type="button" className={`ts-mode-btn ${mode === "Office" ? "active-office" : ""}`}
            onClick={() => mode !== "Office" && handleModeToggle(dayKey)} aria-pressed={mode === "Office"}>Off</button>
          <button type="button" className={`ts-mode-btn ${mode === "WFH" ? "active-wfh" : ""}`}
            onClick={() => mode !== "WFH" && handleModeToggle(dayKey)} aria-pressed={mode === "WFH"}>WFH</button>
        </fieldset>
      ) : (
        <span className={`ts-mode-label ${mode === "WFH" ? "wfh" : "office"}`}>{mode === "WFH" ? "WFH" : "Office"}</span>
      )}
    </>
  );
};

const TimesheetDropdown = ({
  weekStart,
  existingData,
  username,
  startDate,
  approvedLeaveDates = new Set(),
  holidayDates = new Set(),
  fetchSubmittedTimesheets,
  autoOpen = false,
  clientNotesEnabled = false,
  onClose,
}) => {
  const [editMode, setEditMode] = useState(autoOpen);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const buildData = (data, ws) => ({
    timesheetId: data?.timesheetId || null,
    weekStart: ws,
    weekEnd: dayjs(ws).add(6, "day").format("YYYY-MM-DD"),
    status: data?.status || "Not Submitted",
    hoursPerDay: data?.hoursPerDay || { monday:"0",tuesday:"0",wednesday:"0",thursday:"0",friday:"0",saturday:"0",sunday:"0" },
    totalHours: data?.totalHours ?? Object.values(data?.hoursPerDay || {}).reduce((s,h)=>s+Number(h||0),0),
    comment: data?.clientComment || data?.comment || "",
    clientComment: data?.clientComment || data?.comment || "",
    employerComment: data?.employerComment || "",
    workModePerDay: data?.workModePerDay || { monday:"Office",tuesday:"Office",wednesday:"Office",thursday:"Office",friday:"Office",saturday:"Office",sunday:"Office" },
    clientName: data?.clientName || "Internal",
  });

  const [localData, setLocalData] = useState(() => buildData(existingData, weekStart));

  useEffect(() => {
    setLocalData(buildData(existingData, weekStart));
    if (autoOpen) setEditMode(true);
  }, [weekStart, existingData, autoOpen]);

  const canEdit = localData.status !== "Approved";

  const handleChange = (dayKey, value) => {
    if (!canEdit) return;
    const idx = DAYS.indexOf(dayKey);
    const date = dayjs(weekStart).add(idx, "day");
    if (startDate && date.isBefore(dayjs(startDate), "day")) return;
    if (approvedLeaveDates.has(date.format("YYYY-MM-DD"))) return;
    if (holidayDates.has(date.format("YYYY-MM-DD"))) return;
    const updatedHours = { ...localData.hoursPerDay, [dayKey]: value === "" ? "" : Math.min(Number(value), 24) };
    const total = Object.values(updatedHours).reduce((s,h) => s + (h === "" ? 0 : Number(h)), 0);
    setLocalData(prev => ({ ...prev, hoursPerDay: updatedHours, totalHours: total }));
  };

  const handleModeToggle = (dayKey) => {
    if (!canEdit) return;
    setLocalData(prev => ({
      ...prev,
      workModePerDay: { ...prev.workModePerDay, [dayKey]: prev.workModePerDay[dayKey] === "WFH" ? "Office" : "WFH" },
    }));
  };

  const handleSubmit = async () => {
    if (localData.totalHours > 60) return;
    setSubmitting(true);
    setMessage(null);
    const payload = {
      timesheetId: localData.timesheetId,
      weekStart: localData.weekStart,
      weekEnd: localData.weekEnd,
      status: "Pending",
      hoursPerDay: localData.hoursPerDay,
      totalHours: localData.totalHours,
      submittedAt: new Date().toISOString(),
      username,
      comment: localData.clientComment,
      clientComment: localData.clientComment,
      employerComment: localData.employerComment,
      workModePerDay: localData.workModePerDay,
      clientName: localData.clientName,
    };
    try {
      const isNew = localData.status === "Not Submitted";
      const url = isNew ? "/timesheets/operations/create" : `/timesheets/operations/update/${localData.timesheetId}`;
      const method = isNew ? "POST" : "PATCH";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${sessionStorage.getItem("jwtToken")}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setMessage({ type: "success", text: "Timesheet submitted successfully!" });
        await fetchSubmittedTimesheets();
        setEditMode(false);
      } else {
        setMessage({ type: "error", text: "Failed to submit timesheet." });
      }
    } catch {
      setMessage({ type: "error", text: "Error submitting timesheet." });
    } finally {
      setSubmitting(false);
    }
  };

  const statusKey = (localData.status || "Not Submitted").toLowerCase().replace(/\s+/g, "-");

  const getButtonLabel = () => {
    if (localData.status === "Not Submitted") return "Create Timesheet";
    if (localData.status === "Rejected") return "Edit & Resubmit";
    return "View Timesheet";
  };

  if (!editMode) {
    return (
      <div style={{ padding: "12px 20px 14px" }}>
        <motion.button
          className="ts-edit-btn"
          onClick={() => setEditMode(true)}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          <Icon.Eye size={14} color="currentColor" />
          {getButtonLabel()}
        </motion.button>
      </div>
    );
  }

  return (
    <motion.div
      className="ts-dropdown-card"
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* Header */}
      <div className="ts-dropdown-header">
        <div className="ts-week-range">
          <Icon.Calendar size={14} color="#2563EB" />
          <span>{localData.weekStart} – {localData.weekEnd}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className={`ts-status-pill ${statusKey}`}>{localData.status}</span>
          {localData.totalHours > 0 && (
            <span style={{ fontSize: "0.78rem", fontWeight: 700, color: localData.totalHours > 60 ? "var(--error)" : "var(--primary)" }}>
              {localData.totalHours}h total
            </span>
          )}
        </div>
      </div>

      {/* Read-only notice */}
      {!canEdit && (
        <div style={{ margin: "0 16px 0", padding: "8px 14px", borderRadius: 8, background: "#ECFDF5", border: "1px solid #A7F3D0", fontSize: "0.78rem", fontWeight: 600, color: "#059669", display: "flex", alignItems: "center", gap: 8 }}>
          <Icon.CheckCircle size={13} color="#059669" />
          This timesheet has been approved and cannot be edited.
        </div>
      )}

      {/* Days grid */}
      <div className="ts-days-grid">
        {DAYS.map((dayKey, idx) => {
          const date = dayjs(weekStart).add(idx, "day");
          const dateStr = date.format("YYYY-MM-DD");
          const isBeforeStart = startDate ? date.isBefore(dayjs(startDate), "day") : false;
          const isHoliday = holidayDates.has(dateStr);
          const isOnLeave = !isHoliday && approvedLeaveDates.has(dateStr);
          const mode = localData.workModePerDay[dayKey];
          const hours = localData.hoursPerDay[dayKey];

          return (
            <div key={dayKey} className={`ts-day-col`}>
              <div className="ts-day-header">
                <span className="ts-day-name">{DAY_SHORT[idx]}</span>
                <span className="ts-day-date">{date.format("M/D")}</span>
              </div>

              <div className={`ts-day-cell ${isHoliday ? "holiday-day" : (isOnLeave ? "leave-day" : "")}`}>
                <DayCell isOnLeave={isOnLeave} isHoliday={isHoliday} isBeforeStart={isBeforeStart} canEdit={canEdit}
                  hours={hours} mode={mode} dayKey={dayKey}
                  handleChange={handleChange} handleModeToggle={handleModeToggle}/>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer: totals + dual notes */}
      <div className="ts-footer" style={{ flexDirection: "column", alignItems: "stretch", gap: 14 }}>
        <div className="ts-totals">
          <div className="ts-total-badge">
            <Icon.Clock size={14} color="currentColor" />
            Total: {localData.totalHours}h
          </div>
          {localData.totalHours > 60 && (
            <span style={{ fontSize: "0.78rem", color: "var(--error)", fontWeight: 600 }}>
              Exceeds 60h limit
            </span>
          )}
        </div>

        <div className="ts-comments-container">
          {/* Employer Notes — always shown, visible to Employer and Employee only */}
          <div className="ts-comment-wrap">
            <div className="ts-comment-header">
              <span className="ts-comment-label">
                <Icon.FileText size={13} color="#7C3AED" /> Employer Notes
              </span>
            </div>
            <textarea
              className="ts-comment-area"
              placeholder="Add note for employer (optional)..."
              value={localData.employerComment}
              onChange={(e) => canEdit && setLocalData(prev => ({ ...prev, employerComment: e.target.value }))}
              readOnly={!canEdit}
              rows={2}
              aria-label="Employer Notes"
            />
          </div>

          {/* Client Notes — only shown when the employer has enabled Client Notes for this
              employee. Visible to Employee, Employer and Client. */}
          {clientNotesEnabled && (
            <div className="ts-comment-wrap">
              <div className="ts-comment-header">
                <span className="ts-comment-label">
                  <Icon.FileText size={13} color="#2563EB" /> Client Notes
                </span>
              </div>
              <textarea
                className="ts-comment-area"
                placeholder="Add note or work summary for client (optional)..."
                value={localData.clientComment}
                onChange={(e) => canEdit && setLocalData(prev => ({ ...prev, clientComment: e.target.value, comment: e.target.value }))}
                readOnly={!canEdit}
                rows={2}
                aria-label="Client Notes"
              />
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="ts-actions">
        <AnimatePresence>
          {message && (
            <motion.div
              className={message.type === "success" ? "ts-success-msg" : "ts-error-msg"}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {message.type === "success"
                ? <Icon.CheckCircle size={14} color="currentColor" />
                : <Icon.AlertCircle size={14} color="currentColor" />}
              {message.text}
            </motion.div>
          )}
        </AnimatePresence>

        <button
          className="ts-edit-btn"
          onClick={() => (onClose ? onClose() : setEditMode(false))}
          style={{ marginLeft: "auto" }}
        >
          <Icon.X size={13} color="currentColor" /> Close
        </button>

        {canEdit && (
          <motion.button
            className="ts-submit-btn"
            onClick={handleSubmit}
            disabled={submitting || localData.totalHours > 60}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {submitting ? (
              <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
            ) : (
              <>
                <Icon.Check size={14} color="#fff" />
                Submit
              </>
            )}
          </motion.button>
        )}
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </motion.div>
  );
};

export default TimesheetDropdown;
